import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "../store";

export default function Dashboard() {
  const navigate = useNavigate();
  const projects = useStore((s) => s.projects);
  const loading = useStore((s) => s.loading);
  const error = useStore((s) => s.error);
  const fetchProjects = useStore((s) => s.fetchProjects);
  const createProject = useStore((s) => s.createProject);
  const clearError = useStore((s) => s.clearError);

  const [showModal, setShowModal] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleCreate = useCallback(async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const project = await createProject(newName.trim(), newDesc.trim());
      setShowModal(false);
      setNewName("");
      setNewDesc("");
      navigate(`/projects/${project.id}`);
    } catch {
      // error is set in store
    } finally {
      setCreating(false);
    }
  }, [newName, newDesc, createProject, navigate]);

  const totalEnvironments = projects.reduce(
    (sum, p) => sum + (p.environment_count ?? 0),
    0,
  );
  const totalActiveJobs = projects.reduce(
    (sum, p) => sum + (p.active_training_jobs ?? 0),
    0,
  );
  const totalDeployments = projects.reduce(
    (sum, p) => sum + (p.deployment_count ?? 0),
    0,
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">
            Manage your robotics simulation projects
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          + New Project
        </button>
      </div>

      {/* Quick stats */}
      <div className="stat-row">
        <div className="stat-card">
          <div className="stat-value">{projects.length}</div>
          <div className="stat-label">Projects</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{totalEnvironments}</div>
          <div className="stat-label">Environments</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{totalActiveJobs}</div>
          <div className="stat-label">Active Training Jobs</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{totalDeployments}</div>
          <div className="stat-label">Deployments</div>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="card mb-4" style={{ borderColor: "var(--color-danger)" }}>
          <div className="flex justify-between items-center">
            <span style={{ color: "var(--color-danger)" }}>{error}</span>
            <button className="btn btn-sm btn-secondary" onClick={clearError}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Project grid */}
      {loading && projects.length === 0 ? (
        <p className="text-muted">Loading projects...</p>
      ) : (
        <div className="card-grid">
          {projects.map((project) => (
            <div
              key={project.id}
              className="card"
              style={{ cursor: "pointer" }}
              onClick={() => navigate(`/projects/${project.id}`)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter") navigate(`/projects/${project.id}`);
              }}
            >
              <div className="card-header">
                <div>
                  <div className="card-title">{project.name}</div>
                  <div className="card-meta">{project.description}</div>
                </div>
                <span
                  className={`badge ${
                    (project.status ?? "active") === "active"
                      ? "badge-success"
                      : "badge-neutral"
                  }`}
                >
                  {project.status ?? "active"}
                </span>
              </div>
              <div className="flex gap-4 text-sm text-muted">
                <span>{project.environment_count ?? 0} environments</span>
                <span>{project.active_training_jobs ?? 0} training</span>
                <span>{project.deployment_count ?? 0} deployed</span>
              </div>
              {project.updated_at && (
                <div className="card-meta" style={{ marginTop: 8 }}>
                  Updated{" "}
                  {new Date(project.updated_at).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </div>
              )}
            </div>
          ))}
          {projects.length === 0 && !loading && (
            <div className="card" style={{ gridColumn: "1 / -1", textAlign: "center" }}>
              <p className="text-muted" style={{ padding: 32 }}>
                No projects yet. Create your first project to get started.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Create project modal */}
      {showModal && (
        <div
          className="modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowModal(false);
          }}
        >
          <div className="modal">
            <h2 className="modal-title">Create New Project</h2>
            <div className="form-group">
              <label className="form-label" htmlFor="project-name">
                Project Name
              </label>
              <input
                id="project-name"
                className="form-input"
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g., Warehouse Cell Alpha"
                autoFocus
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="project-desc">
                Description
              </label>
              <textarea
                id="project-desc"
                className="form-textarea"
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                placeholder="Brief description of the project scope..."
              />
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCreate}
                disabled={!newName.trim() || creating}
              >
                {creating ? "Creating..." : "Create Project"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
